/**
 * Top-right toast stack. Replaces noisy `alert()` calls and keeps the page
 * interactive while feedback shows.
 */

export type ToastKind = 'info' | 'success' | 'warn' | 'error';

const STYLES = `
  .gh-toast-host {
    position: fixed;
    top: 14px;
    right: 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    z-index: 5000;
    pointer-events: none;
  }
  .gh-toast {
    pointer-events: auto;
    background: var(--panel, #161a22);
    border: 1px solid var(--border, #262c38);
    color: var(--text, #e5e9f0);
    border-left: 3px solid var(--accent, #f5c84b);
    border-radius: 6px;
    padding: 9px 14px;
    min-width: 240px;
    max-width: 360px;
    font-size: 13px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    animation: gh-toast-in 160ms ease-out;
  }
  .gh-toast.success { border-left-color: var(--success, #4ade80); }
  .gh-toast.warn    { border-left-color: var(--warn, #f97316); }
  .gh-toast.error   { border-left-color: var(--error, #ef4444); }
  .gh-toast.fading  { animation: gh-toast-out 200ms ease-in forwards; }
  @keyframes gh-toast-in {
    from { transform: translateX(20px); opacity: 0; }
    to   { transform: translateX(0); opacity: 1; }
  }
  @keyframes gh-toast-out {
    from { transform: translateX(0); opacity: 1; }
    to   { transform: translateX(20px); opacity: 0; }
  }
`;

let stylesInjected = false;
function ensureHost(): HTMLElement {
  if (!stylesInjected) {
    const style = document.createElement('style');
    style.id = 'gh-toast-styles';
    style.textContent = STYLES;
    document.head.appendChild(style);
    stylesInjected = true;
  }
  let host = document.querySelector<HTMLElement>('.gh-toast-host');
  if (!host) {
    host = document.createElement('div');
    host.className = 'gh-toast-host';
    document.body.appendChild(host);
  }
  return host;
}

const MAX_VISIBLE_TOASTS = 5;

export function showToast(text: string, kind: ToastKind = 'info', ttlMs = 4000): void {
  const host = ensureHost();

  // Dedupe: if the most recent visible toast has the same text+kind, bump its
  // counter instead of stacking a new one. Helps rapid-fire bursts.
  const last = host.lastElementChild as HTMLElement | null;
  if (last && last.dataset.text === text && last.dataset.kind === kind && !last.classList.contains('fading')) {
    const next = Number(last.dataset.count ?? '1') + 1;
    last.dataset.count = String(next);
    last.textContent = `${text} ×${next}`;
    return;
  }

  // Cap visible stack: if we exceed the cap, fade the oldest first.
  while (host.children.length >= MAX_VISIBLE_TOASTS) {
    const oldest = host.firstElementChild as HTMLElement | null;
    if (!oldest) break;
    oldest.remove();
  }

  const el = document.createElement('div');
  el.className = `gh-toast ${kind}`;
  el.textContent = text;
  el.dataset.text = text;
  el.dataset.kind = kind;
  el.dataset.count = '1';
  host.appendChild(el);

  const dismiss = (): void => {
    el.classList.add('fading');
    setTimeout(() => el.remove(), 220);
  };
  el.addEventListener('click', dismiss);
  setTimeout(dismiss, ttlMs);
}

/** Convenience for catch blocks. */
export function showError(err: unknown): void {
  const message = err instanceof Error ? err.message : String(err);
  showToast(message, 'error', 6000);
}
